import { useCallback, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  InvitationForm,
  InvitationFormData,
} from "@/app/components/invitation/InvitationForm";
import {
  RenterMetaForm,
  RenterMetaFormData,
} from "@/app/components/invitation/RenterMetaForm";
import { InvitationReview } from "@/app/components/invitation/InvitationReview";
import { Stepper } from "@/app/components/initiate/Stepper";
import { ModalError } from "@/app/components/modals/modalBuilder";
import { InvitationLinkModal } from "@/app/components/modals/invitation/invitationLinkModal";
import { useDisclosure } from "@heroui/react";
import useErrorHandling from "@/hooks/useErrorHandling";
import { api } from "@/api";
import { InvitationCreateNew } from "@/api/invitations";
import { useAppSelector } from "@/hooks/useAppSelector";
import {
  OfficesSelectionForm,
  OfficesSelectionFormData,
} from "@/app/components/invitation/OfficesSelectionForm";

export function CreateNewInvitationPage() {
  const navigate = useNavigate();
  const handleError = useErrorHandling();
  const businessProfileId = useAppSelector(
    (state) => state.businessProfile.businessProfile.id,
  );
  const [activeStep, setActiveStep] = useState(0);
  const [invitationData, setInvitationData] =
    useState<InvitationFormData | null>(null);
  const [officesData, setOfficesData] =
    useState<OfficesSelectionFormData | null>(null);
  const [renterMetaData, setRenterMetaData] =
    useState<RenterMetaFormData | null>(null);
  const [modalErrors, setModalErrors] = useState<ModalError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitationLink, setInvitationLink] = useState<string>("");
  const linkModalDisclosure = useDisclosure();
  const wasModalOpenRef = useRef(false);

  const steps = [
    {
      name: "Данные приглашения",
      description: "Заполните основную информацию",
      longDescription: "Укажите параметры договора",
    },
    {
      name: "Офисы",
      description: "Выберите офисы и рабочие места",
      longDescription: "Укажите офисы и рабочие места для договора",
    },
    {
      name: "Данные арендатора",
      description: "Заполните информацию об арендаторе",
      longDescription:
        "Введите реквизиты и контактные данные нового арендатора",
    },
    {
      name: "Проверка данных",
      description: "Проверьте введенные данные",
      longDescription: "Убедитесь, что все данные корректны перед отправкой",
    },
  ];

  const handleInvitationSubmit = useCallback((data: InvitationFormData) => {
    setInvitationData(data);
    setModalErrors([]);
    setActiveStep(1); // Переход к шагу выбора офисов
  }, []);

  const handleOfficesSubmit = useCallback((data: OfficesSelectionFormData) => {
    setOfficesData(data);
    setModalErrors([]);
    setActiveStep(2); // Переход к шагу данных арендатора
  }, []);

  const handleRenterMetaSubmit = useCallback((data: RenterMetaFormData) => {
    setRenterMetaData(data);
    setModalErrors([]);
    setActiveStep(3); // Переход к проверке данных
  }, []);

  const handleReviewSubmit = useCallback(async () => {
    if (!invitationData || !renterMetaData || !officesData) return;

    setIsSubmitting(true);

    try {
      // Разделяем офисы и рабочие места
      const offices = officesData.offices
        .filter((item) => item.office_id !== null && !item.workplace_id)
        .map((item) => ({
          office_id: item.office_id!,
          price: item.price,
        }));

      const workplaces = officesData.offices
        .filter((item) => item.workplace_id !== null)
        .map((item) => ({
          workplace_id: item.workplace_id!,
          price: item.price,
        }));

      const payload: InvitationCreateNew = {
        business_profile_id: businessProfileId,
        start_date: invitationData.startDate,
        end_date: invitationData.endDate,
        meetingHours: invitationData.meetingHours,
        offices: offices,
        workplaces: workplaces,
        requisite: invitationData.requisite,
        type: "new_renter",
        number: invitationData.number,
        meta: {
          renter_name: renterMetaData.renter_name,
          address: renterMetaData.address,
          ogrn: renterMetaData.ogrn,
          inn: renterMetaData.inn,
          kpp: renterMetaData.kpp,
          bik: renterMetaData.bik,
          bank_account: renterMetaData.bank_account,
          correspondent_account: renterMetaData.correspondent_account,
          bank_name: renterMetaData.bank_name,
          phone_number: renterMetaData.phone_number,
          email: renterMetaData.email,
        },
      };

      const response = await api.invitations.create(payload);
      const link = `https://app.elasticspace.app/invitations/${response.data.opaque_id}`;
      setInvitationLink(link);
      setIsSubmitting(false);
      linkModalDisclosure.onOpen();
    } catch (err: any) {
      const { errorMessage, parsedErrors } = handleError(err);
      setModalErrors(parsedErrors || []);
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  }, [
    invitationData,
    officesData,
    renterMetaData,
    businessProfileId,
    linkModalDisclosure,
    handleError,
  ]);

  const handleEdit = useCallback(
    (step?: number) => {
      if (step !== undefined) {
        setActiveStep(step);
      } else {
        if (renterMetaData) {
          setActiveStep(2); // Редактирование данных арендатора
        } else if (officesData) {
          setActiveStep(1); // Редактирование офисов
        } else {
          setActiveStep(0); // Редактирование данных приглашения
        }
      }
    },
    [renterMetaData, officesData],
  );

  const changeStep = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  useEffect(() => {
    if (linkModalDisclosure.isOpen) {
      wasModalOpenRef.current = true;
    } else if (wasModalOpenRef.current) {
      // Модалка была открыта и теперь закрыта - делаем редирект
      navigate("/dashboard/invitations");
    }
  }, [linkModalDisclosure.isOpen, navigate]);

  return (
    <>
      <InvitationLinkModal
        disclosure={linkModalDisclosure}
        invitationLink={invitationLink}
      />

      <h1 className="text-xl font-bold mb-4 mt-2">Создание приглашения</h1>

      <div className="flex flex-col lg:flex-row gap-4 w-full">
        <div className="lg:w-1/3">
          <Stepper
            steps={steps}
            activeStep={activeStep}
            changeStep={changeStep}
            colorScheme="primary"
          />
        </div>

        <div className="lg:w-2/3">
          <div className="w-full h-full p-8 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl bg-background">
            <div className="flex flex-col gap-4">
              {!(
                "hideTitle" in steps[activeStep] && steps[activeStep].hideTitle
              ) && (
                <div className="flex flex-col gap-2">
                  <span className="text-lg font-semibold">
                    {steps[activeStep].name}
                  </span>
                  <span className="text-sm text-gray-500">
                    {steps[activeStep].longDescription}
                  </span>
                </div>
              )}

              {activeStep === 0 && (
                <InvitationForm
                  type="new_renter"
                  defaultValues={invitationData || undefined}
                  onSubmit={handleInvitationSubmit}
                  modalErrors={modalErrors}
                />
              )}

              {activeStep === 1 && (
                <OfficesSelectionForm
                  defaultValues={officesData || undefined}
                  onSubmit={handleOfficesSubmit}
                  modalErrors={modalErrors}
                />
              )}

              {activeStep === 2 && (
                <RenterMetaForm
                  defaultValues={renterMetaData || undefined}
                  onSubmit={handleRenterMetaSubmit}
                  modalErrors={modalErrors}
                />
              )}

              {activeStep === 3 && (
                <InvitationReview
                  invitationData={invitationData || ({} as InvitationFormData)}
                  renterMetaData={renterMetaData || undefined}
                  officesData={officesData || undefined}
                  onEdit={handleEdit}
                  onSubmit={handleReviewSubmit}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
